import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { useAuth } from "@/features/auth/AuthContext";
import { absoluteUrl } from "@/lib/seo";

export const Route = createFileRoute("/acceso")({
  head: () => ({
    meta: [
      { title: "Acceso | Socrattica" },
      {
        name: "description",
        content: "Inicia sesión o crea tu cuenta de Socrattica.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/acceso") }],
  }),
  component: AccessPage,
});

type AccessMode = "login" | "register";
type PendingAction = "google" | "email" | "register" | "reset" | null;

function getAuthErrorMessage(error: unknown) {
  const code =
    error && typeof error === "object" && "code" in error ? String(error.code) : undefined;

  switch (code) {
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "El correo o la contraseña no son correctos.";
    case "auth/email-already-in-use":
      return "Ya existe una cuenta con este correo. Intenta iniciar sesión.";
    case "auth/weak-password":
      return "Usa una contraseña más segura de al menos 8 caracteres.";
    case "auth/invalid-email":
      return "Escribe un correo electrónico válido.";
    case "auth/popup-closed-by-user":
      return "Cerraste la ventana de Google antes de terminar.";
    case "auth/popup-blocked":
      return "El navegador bloqueó la ventana de Google. Permite las ventanas emergentes.";
    case "auth/account-exists-with-different-credential":
      return "Este correo ya está registrado con otro método de acceso.";
    case "auth/too-many-requests":
      return "Hiciste varios intentos. Espera unos minutos y vuelve a intentarlo.";
    case "auth/network-request-failed":
      return "No pudimos conectarnos. Revisa tu conexión a internet.";
    default:
      return error instanceof Error ? error.message : "No pudimos completar la solicitud.";
  }
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 text-black">
      <path
        fill="currentColor"
        d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.91h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.4Z"
      />
      <path
        fill="currentColor"
        d="M12 22c2.7 0 4.98-.9 6.63-2.43l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.05v2.62A10 10 0 0 0 12 22Z"
      />
      <path
        fill="currentColor"
        d="M6.39 13.86A6.02 6.02 0 0 1 6.08 12c0-.65.11-1.28.31-1.86V7.52H3.05A10 10 0 0 0 2 12c0 1.61.39 3.14 1.05 4.48l3.34-2.62Z"
      />
      <path
        fill="currentColor"
        d="M12 6.01c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.65 9.65 0 0 0 12 2a10 10 0 0 0-8.95 5.52l3.34 2.62C7.18 7.77 9.39 6.01 12 6.01Z"
      />
    </svg>
  );
}

function AccessPage() {
  const navigate = useNavigate();
  const {
    user,
    role,
    isAdmin,
    isLoading,
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    resetPassword,
  } = useAuth();
  const [mode, setMode] = useState<AccessMode>("login");
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  useEffect(() => {
    if (isLoading || !user) return;
    void navigate({
      to: isAdmin || role === "admin" ? "/admin/dashboard" : "/cuenta",
      replace: true,
    });
  }, [isAdmin, isLoading, navigate, role, user]);

  async function runAction(
    action: Exclude<PendingAction, null>,
    callback: () => Promise<void>,
    successMessage?: string,
  ) {
    setPendingAction(action);

    try {
      await callback();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
    } finally {
      setPendingAction(null);
    }
  }

  function handleEmailLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    void runAction(
      "email",
      () => signInWithEmail(email, password),
      "Sesión iniciada correctamente.",
    );
  }

  function handleRegister(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get("name") ?? "");
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    const confirmation = String(formData.get("passwordConfirmation") ?? "");

    if (password !== confirmation) {
      const message = "Las contraseñas no coinciden.";
      toast.error(message);
      return;
    }

    void runAction(
      "register",
      () => registerWithEmail(name, email, password),
      "Cuenta creada. Revisa tu correo para verificarla.",
    );
  }

  function handleResetPassword(event: React.MouseEvent<HTMLButtonElement>) {
    const form = event.currentTarget.form;
    const emailInput = form?.elements.namedItem("email");
    const email = emailInput instanceof HTMLInputElement ? emailInput.value : "";

    if (!email.trim()) {
      const message = "Escribe tu correo para enviarte el enlace de recuperación.";
      toast.error(message);
      return;
    }

    void runAction(
      "reset",
      () => resetPassword(email),
      "Te enviamos un enlace para restablecer tu contraseña.",
    );
  }

  const isPending = pendingAction !== null;

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex flex-1 items-center px-4 py-6 sm:px-6 sm:py-8">
        <div className="mx-auto grid w-full max-w-4xl overflow-hidden rounded-xl border border-black/15 bg-white md:grid-cols-[0.9fr_1.1fr]">
          <section className="relative hidden min-h-[540px] overflow-hidden bg-black p-6 text-white md:flex md:flex-col md:justify-between lg:p-8">
            <Link
              to="/"
              className="relative inline-flex w-fit items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <span
                aria-hidden="true"
                className="h-9 w-9 rounded-full bg-[url('/logo.png')] bg-cover bg-center"
              />
              <span className="font-display text-xl font-semibold">Socrattica</span>
            </Link>

            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white/70">
                <ShieldCheck className="h-3.5 w-3.5" />
                Tu espacio
              </span>
              <h1 className="mt-3 max-w-sm text-3xl font-semibold leading-tight tracking-[-0.05em]">
                Tu aprendizaje, en un solo lugar.
              </h1>
              <p className="mt-3 max-w-xs text-xs leading-5 text-white/55">
                Una sola cuenta para acceder a tus compras, descargas y próximos recursos de
                Socrattica.
              </p>
            </div>
          </section>

          <section className="flex min-h-[540px] items-center px-5 py-6 sm:px-7 lg:px-10">
            <div className="mx-auto w-full max-w-sm">
              <Link
                to="/"
                className="mb-5 inline-flex items-center gap-2 text-xs text-black/45 transition hover:text-black md:hidden"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver a Socrattica
              </Link>

              {isLoading || Boolean(user) ? (
                <div className="flex min-h-96 items-center justify-center" role="status">
                  <LoaderCircle className="h-6 w-6 animate-spin text-black" />
                  <span className="sr-only">Cargando tu cuenta</span>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/55">
                      Bienvenido
                    </p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-[-0.04em]">
                      {mode === "login" ? "Entra a tu cuenta" : "Crea tu cuenta"}
                    </h1>
                  </div>

                  <div className="mt-3 grid grid-cols-2 rounded-lg bg-[#f1f3f7] p-1">
                    {(["login", "register"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setMode(option)}
                        className={`h-9 cursor-pointer rounded-md text-xs font-semibold transition ${
                          mode === option
                            ? "bg-white text-black shadow-sm"
                            : "text-black/45 hover:text-black"
                        }`}
                      >
                        {option === "login" ? "Iniciar sesión" : "Registrarme"}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() =>
                      void runAction("google", signInWithGoogle, "Sesión iniciada con Google.")
                    }
                    className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-3 rounded-lg border border-black/10 bg-white text-xs font-semibold shadow-sm transition hover:border-black/20 hover:bg-black/[0.02] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {pendingAction === "google" ? (
                      <LoaderCircle className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleIcon />
                    )}
                    Continuar con Google
                  </button>
                  <p className="mt-2 text-center text-xs leading-5 text-black/55">
                    Al continuar con Google aceptas los{" "}
                    <Link to="/legal/terminos" className="font-medium text-black underline">
                      términos
                    </Link>{" "}
                    y confirmas haber leído la{" "}
                    <Link to="/legal/privacidad" className="font-medium text-black underline">
                      política de privacidad
                    </Link>
                    .
                  </p>

                  <div className="my-1.5 flex items-center gap-4">
                    <span className="h-px flex-1 bg-black/10" />
                    <span className="text-xs text-black/35">o usa tu correo</span>
                    <span className="h-px flex-1 bg-black/10" />
                  </div>

                  {mode === "login" ? (
                    <form className="space-y-3" onSubmit={handleEmailLogin}>
                      <AuthField
                        label="Correo electrónico"
                        name="email"
                        type="email"
                        placeholder="tu@correo.com"
                        autoComplete="email"
                        icon={<Mail className="h-4 w-4" />}
                      />
                      <AuthField
                        label="Contraseña"
                        name="password"
                        type="password"
                        placeholder="Tu contraseña"
                        autoComplete="current-password"
                        icon={<LockKeyhole className="h-4 w-4" />}
                      />
                      <div className="flex justify-end">
                        <button
                          type="button"
                          disabled={isPending}
                          onClick={handleResetPassword}
                          className="cursor-pointer text-xs font-medium text-black hover:underline disabled:opacity-50"
                        >
                          ¿Olvidaste tu contraseña?
                        </button>
                      </div>
                      <SubmitButton isPending={pendingAction === "email"}>
                        Iniciar sesión
                      </SubmitButton>
                    </form>
                  ) : (
                    <form className="space-y-3" onSubmit={handleRegister}>
                      <AuthField
                        label="Nombre"
                        name="name"
                        type="text"
                        placeholder="Tu nombre"
                        autoComplete="name"
                        minLength={2}
                        icon={<UserRound className="h-4 w-4" />}
                      />
                      <AuthField
                        label="Correo electrónico"
                        name="email"
                        type="email"
                        placeholder="tu@correo.com"
                        autoComplete="email"
                        icon={<Mail className="h-4 w-4" />}
                      />
                      <div className="grid gap-4 sm:grid-cols-2">
                        <AuthField
                          label="Contraseña"
                          name="password"
                          type="password"
                          placeholder="Mínimo 8 caracteres"
                          autoComplete="new-password"
                          minLength={8}
                          icon={<LockKeyhole className="h-4 w-4" />}
                        />
                        <AuthField
                          label="Confirmar"
                          name="passwordConfirmation"
                          type="password"
                          placeholder="Repítela"
                          autoComplete="new-password"
                          minLength={8}
                          icon={<CheckCircle2 className="h-4 w-4" />}
                        />
                      </div>
                      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-black/55">
                        <input
                          type="checkbox"
                          name="legal-consent"
                          required
                          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-black"
                        />
                        <span>
                          Acepto los{" "}
                          <Link to="/legal/terminos" className="font-medium text-black underline">
                            términos de uso
                          </Link>{" "}
                          y confirmo que leí la{" "}
                          <Link to="/legal/privacidad" className="font-medium text-black underline">
                            política de privacidad y tratamiento de datos
                          </Link>
                          .
                        </span>
                      </label>
                      <SubmitButton isPending={pendingAction === "register"}>
                        Crear mi cuenta
                      </SubmitButton>
                    </form>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function AuthField({
  label,
  icon,
  ...inputProps
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-black">{label}</span>
      <span className="relative block">
        <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-black/35">
          {icon}
        </span>
        <input
          {...inputProps}
          required
          className="h-10 w-full rounded-lg border border-black/20 bg-white pl-11 pr-4 text-xs text-black outline-none transition placeholder:text-black/30 focus:border-black focus:ring-2 focus:ring-black/10"
        />
      </span>
    </label>
  );
}

function SubmitButton({ children, isPending }: { children: React.ReactNode; isPending: boolean }) {
  return (
    <button
      type="submit"
      disabled={isPending}
      className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-black bg-black px-5 text-xs font-semibold text-white transition hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}
